import { useEffect, useRef, useState } from "react";
import { X, Download, ExternalLink, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EVT = "logerd:view-file";
const CONVERTIBLE = /\.(docx?|docm|dot|xlsx?|xlsm|xlt|pptx?|pps|pot|csv|heic|heif)$/i;
const CONVERTIBLE_MIME = /officedocument|ms-?excel|ms-?powerpoint|msword|text\/csv|image\/hei[cf]/i;
/** Office documents and HEIC photos are converted server-side to HTML / JPEG for in-browser preview. */
export const canPreview = (filename = "", mime = "") => CONVERTIBLE.test(filename) || CONVERTIBLE_MIME.test(mime);

/** Open a fetched Blob in the in-app viewer (works with pop-up blockers & Safari). */
export function viewBlob(blob, { title = "Document", filename = "" } = {}) {
  window.dispatchEvent(new CustomEvent(EVT, { detail: { blob, title, filename } }));
}

/** Fetch with an axios client and show in the viewer. */
export async function viewFromApi(client, url, { params, title, filename, type } = {}) {
  try {
    const res = await client.get(url, { params, responseType: "blob" });
    const mime = type || res.headers?.["content-type"] || res.data.type || "application/octet-stream";
    const cd = res.headers?.["content-disposition"] || "";
    const m = /filename="?([^";]+)"?/.exec(cd);
    viewBlob(new Blob([res.data], { type: mime.split(";")[0] }), { title, filename: filename || (m ? m[1] : "") });
    return true;
  } catch (e) {
    toast.error(e.response?.status === 404 ? "File not found" : "Could not open the file");
    return false;
  }
}

/** iOS Safari paints PDFs inside iframes at 1:1 points (612px wide for Letter), left-aligned and unscaled — scale to fit and centre. */
function IosPdf({ url, title }) {
  const box = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const fit = () => { const w = box.current?.clientWidth || 612; setScale(Math.max(0.5, Math.min(2.2, (w - 8) / 612))); };
    fit(); window.addEventListener("resize", fit); return () => window.removeEventListener("resize", fit);
  }, []);
  return (
    <div ref={box} className="h-full w-full overflow-auto rounded-lg bg-white" style={{ WebkitOverflowScrolling: "touch" }} data-testid="file-viewer-ios-pdf">
      <iframe title={title} src={url} style={{ width: 612, height: `${100 / scale}%`, border: 0, transform: `scale(${scale})`, transformOrigin: "top left", marginLeft: Math.max(0, ((box.current?.clientWidth || 612) - 612 * scale) / 2) }} />
    </div>
  );
}

export function FileViewerHost() {
  const [doc, setDoc] = useState(null);
  const [url, setUrl] = useState("");

  useEffect(() => {
    const on = (e) => setDoc(e.detail);
    window.addEventListener(EVT, on);
    return () => window.removeEventListener(EVT, on);
  }, []);
  useEffect(() => {
    if (!doc) { setUrl(""); return undefined; }
    const native = doc.blob.type.startsWith("image/") && !/hei[cf]/.test(doc.blob.type) || doc.blob.type === "application/pdf" || doc.blob.type === "text/html";
    if (native || !canPreview(doc.filename, doc.blob.type)) {
      const u = URL.createObjectURL(doc.blob);
      setUrl(u);
      return () => URL.revokeObjectURL(u);
    }
    let u = "";
    let alive = true;
    const fd = new FormData();
    fd.append("file", doc.blob, doc.filename || "document");
    api.post("/files/preview", fd, { responseType: "blob" }).then((res) => {
      if (!alive) return;
      const mime = (res.headers?.["content-type"] || "").split(";")[0] || "text/html";
      u = URL.createObjectURL(new Blob([res.data], { type: mime }));
      setDoc((d) => (d === doc ? { ...d, preview: mime } : d));
      setUrl(u);
    }).catch((e) => {
      if (!alive) return;
      toast.error(e.response?.status === 415 ? "Preview not supported for this file type" : "Could not build a preview — download the file instead");
      setDoc((d) => (d === doc ? { ...d, preview: "none" } : d));
      setUrl(URL.createObjectURL(doc.blob));
    });
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [doc]);
  useEffect(() => {
    if (!doc) return undefined;
    const onKey = (e) => e.key === "Escape" && setDoc(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doc]);

  if (!doc) return null;
  const converting = !url && !doc.preview && canPreview(doc.filename, doc.blob.type) && !(doc.blob.type.startsWith("image/") && !/hei[cf]/.test(doc.blob.type));
  const shown = doc.preview && doc.preview !== "none" ? doc.preview : doc.blob.type;
  const isImg = shown.startsWith("image/") && !/hei[cf]/.test(shown);
  const isPdf = shown === "application/pdf";
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isHtml = shown === "text/html";
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(doc.blob); a.download = doc.filename || (isPdf ? "document.pdf" : isImg ? "image" : "file");
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  };

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-slate-900/95 safe-area" style={{ pointerEvents: "auto" }} data-testid="file-viewer" onClick={() => setDoc(null)}>
      <div className="flex items-center justify-between gap-3 px-4 h-14 shrink-0 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0 font-display font-semibold truncate" data-testid="file-viewer-title">{doc.title}{doc.filename ? <span className="ml-2 text-xs font-normal text-slate-400">{doc.filename}</span> : null}</div>
        <div className="flex items-center gap-1">
          {(!isHtml || doc.preview) && <button onClick={download} data-testid="file-viewer-download" title="Download" className="h-9 w-9 rounded-lg hover:bg-white/10 inline-flex items-center justify-center"><Download className="h-4 w-4" /></button>}
          {isHtml && <button onClick={() => { const w = document.querySelector('[data-testid="file-viewer-frame"]'); w?.contentWindow?.print(); }} data-testid="file-viewer-print" title="Print" className="h-9 w-9 rounded-lg hover:bg-white/10 inline-flex items-center justify-center"><Printer className="h-4 w-4" /></button>}
          {isPdf && !isIOS && <button onClick={() => { const w = document.querySelector('[data-testid="file-viewer-pdf"]'); try { w?.contentWindow?.focus(); w?.contentWindow?.print(); } catch { window.open(url, "_blank"); } }} data-testid="file-viewer-print" title="Print" className="h-9 w-9 rounded-lg hover:bg-white/10 inline-flex items-center justify-center"><Printer className="h-4 w-4" /></button>}
          <a href={url} target="_blank" rel="noreferrer" data-testid="file-viewer-newtab" title="Open in new tab" className="h-9 w-9 rounded-lg hover:bg-white/10 inline-flex items-center justify-center"><ExternalLink className="h-4 w-4" /></a>
          <button onClick={() => setDoc(null)} data-testid="file-viewer-close" title="Close" className="h-9 w-9 rounded-lg hover:bg-white/10 inline-flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>
      </div>
      <div className="flex-1 min-h-0 p-3 pt-0 overflow-auto" style={{ WebkitOverflowScrolling: "touch" }} onClick={(e) => e.stopPropagation()}>
        {!url && <div className="h-full flex flex-col items-center justify-center gap-3 text-white" data-testid="file-viewer-loading"><Loader2 className="h-6 w-6 animate-spin" />{converting && <span className="text-xs text-slate-300">Preparing preview…</span>}</div>}
        {url && isImg && <div className="h-full flex items-center justify-center"><img src={url} alt={doc.title} className="max-h-full max-w-full rounded-lg shadow-2xl object-contain" /></div>}
        {url && isPdf && !isIOS && <iframe title={doc.title} src={`${url}#toolbar=1`} data-testid="file-viewer-pdf" className="h-full w-full rounded-lg bg-white" />}
        {url && isPdf && isIOS && <IosPdf url={url} title={doc.title} />}
        {url && isHtml && <iframe title={doc.title} src={url} data-testid="file-viewer-frame" sandbox={doc.preview ? "allow-same-origin allow-modals" : undefined} className="h-full w-full rounded-lg bg-slate-100" />}
        {url && !isImg && !isPdf && !isHtml && (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-white">
            <div className="text-sm text-slate-300">This file type can't be previewed here.</div>
            <button onClick={download} className="rounded-lg bg-white text-slate-900 px-4 h-10 text-sm font-semibold inline-flex items-center gap-2"><Download className="h-4 w-4" /> Download</button>
          </div>
        )}
      </div>
    </div>
  );
}
